import { useCallback, useEffect, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import DateRangePresets, { presetToRange } from "@/components/DateRangePresets";
import { CircleNotch, ArrowsClockwise, Database } from "@phosphor-icons/react";
import { toast } from "sonner";

// FlexOffers' 5 raw statuses, with their semantic intent + billing
// policy. The backend's `/admin/flexoffers/reconciliation` returns
// these in this fixed order so the grid never reorders between renders.
const BUCKET_META = {
  "Approved": {
    desc: "Network confirmed payable. Counts toward both gross and approved-only billing.",
    badge: "bg-emerald-50 text-emerald-800 border-emerald-200",
    bar: "bg-emerald-500",
  },
  "Pending": {
    desc: "Awaiting network finalisation. Gross attributed today, may flip to Approved or Cancelled.",
    badge: "bg-amber-50 text-amber-800 border-amber-200",
    bar: "bg-amber-500",
  },
  "Bonus": {
    desc: "Network-issued bonus revenue. Counted as Approved for billing — tagged here for visibility.",
    badge: "bg-violet-50 text-violet-800 border-violet-200",
    bar: "bg-violet-500",
  },
  "Cancelled": {
    desc: "Network reversed the sale. Excluded from billing.",
    badge: "bg-rose-50 text-rose-800 border-rose-200",
    bar: "bg-rose-500",
  },
  "Non-Commission": {
    desc: "Network flagged as non-commissionable on ingest. Visible but never payable.",
    badge: "bg-slate-100 text-slate-700 border-slate-300",
    bar: "bg-slate-400",
  },
};

const fmtMoney = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtNum = (n) => Number(n || 0).toLocaleString("en-US");

export default function AdminFlexOffersReconciliation() {
  const [dateRange, setDateRange] = useState(() => {
    const r = presetToRange("30d") || { start: "", end: "" };
    return { preset: "30d", start: r.start, end: r.end };
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      const { data: resp } = await API.get(
        `/admin/flexoffers/reconciliation?${params.toString()}`,
      );
      setData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reconciliation");
    } finally {
      setLoading(false);
    }
  }, [dateRange.start, dateRange.end]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Trigger an immediate FlexOffers actions sync for the current
  // window — this is the only way to retroactively pull in the
  // Non-Commission rows we used to drop on ingest.
  const triggerSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const body = {};
      if (dateRange.start) body.start_date = dateRange.start;
      if (dateRange.end) body.end_date = dateRange.end;
      const { data: r } = await API.post("/admin/flexoffers/sync-actions", body);
      toast.success(
        `Synced ${r?.counts?.synced || 0} rows from FlexOffers (${dateRange.start || "default"} → ${dateRange.end || "default"}).`,
      );
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // One-time legacy backfill of network_state on pre-2026-06-17 rows.
  // Safe to re-run — only writes rows missing the field.
  const triggerBackfill = async () => {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const { data: r } = await API.post("/admin/flexoffers/backfill-network-state", {});
      toast.success(
        `Backfilled network_state on ${fmtNum(r.total_modified)} legacy rows (Approved=${r.backfilled.approved}, Pending=${r.backfilled.pending}, Cancelled=${r.backfilled.cancelled}, Bonus=${r.backfilled.bonus}).`,
      );
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const buckets = data?.buckets || [];
  const totals = data?.totals || { count: 0, sale_amount: 0, payout: 0 };
  const maxBucketPayout = Math.max(1, ...buckets.map(b => b.payout || 0));

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="flexoffers-reconciliation-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-baseline gap-3 mb-1">
          <h1 className="text-2xl font-bold text-slate-900">FlexOffers Reconciliation</h1>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200">
            flexoffers
          </span>
        </div>
        <p className="text-sm text-slate-600 max-w-3xl">
          All 5 raw FlexOffers statuses surfaced exactly as the network reports them.
          Our internal 3-bucket billing state (APPROVED / PENDING / REVERSED) collapses these
          — this view keeps the upstream signal intact so you can reconcile against FlexOffers&apos;
          own dashboard line-for-line.
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <DateRangePresets value={dateRange} onChange={setDateRange} />
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          onClick={triggerBackfill}
          disabled={backfilling}
          data-testid="flexoffers-backfill-btn"
          title="One-time backfill: populates network_state on pre-2026-06-17 rows. Safe to re-run."
        >
          {backfilling ? <><CircleNotch size={16} className="mr-1.5 animate-spin" />Backfilling…</>
            : <><Database size={16} className="mr-1.5" />Backfill legacy</>}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={triggerSync}
          disabled={syncing}
          data-testid="flexoffers-sync-btn"
          title="Pull FlexOffers actions for the selected window — only way to retro-fetch Non-Commission rows."
        >
          {syncing ? <><CircleNotch size={16} className="mr-1.5 animate-spin" />Syncing…</>
            : <><ArrowsClockwise size={16} className="mr-1.5" />Re-sync window</>}
        </Button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Rows in window</div>
          <div className="text-2xl font-bold font-mono text-slate-900" data-testid="recon-total-rows">{fmtNum(totals.count)}</div>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Total sale amount</div>
          <div className="text-2xl font-bold font-mono text-slate-900" data-testid="recon-total-sale">{fmtMoney(totals.sale_amount)}</div>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Total payout</div>
          <div className="text-2xl font-bold font-mono text-[#002FA7]" data-testid="recon-total-payout">{fmtMoney(totals.payout)}</div>
        </div>
      </div>

      {/* 5-bucket grid */}
      {loading ? (
        <div className="py-12 flex items-center justify-center text-slate-500">
          <CircleNotch size={20} className="mr-2 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-3" data-testid="recon-buckets">
          {buckets.map((b) => {
            const meta = BUCKET_META[b.network_state] || {
              desc: "Unexpected upstream status — investigate.",
              badge: "bg-slate-100 text-slate-700 border-slate-300",
              bar: "bg-slate-400",
            };
            const pct = totals.payout > 0 ? (b.payout / totals.payout) * 100 : 0;
            return (
              <div
                key={b.network_state}
                className="border border-slate-200 rounded-md bg-white overflow-hidden"
                data-testid={`recon-bucket-${b.network_state.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex items-center px-4 py-3 gap-4">
                  <div className="w-44 shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${meta.badge}`}>
                      {b.network_state}
                    </span>
                    <div className="mt-1 flex gap-1 text-[9px] uppercase tracking-wider">
                      {b.is_billable_approved_only && (
                        <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">payable</span>
                      )}
                      {b.is_billable_gross && !b.is_billable_approved_only && (
                        <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">gross only</span>
                      )}
                      {!b.is_billable_gross && (
                        <span className="px-1 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">excluded</span>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-600 mb-2">{meta.desc}</div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${meta.bar} transition-all duration-500`}
                        style={{ width: `${(b.payout / maxBucketPayout) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-28 text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Rows</div>
                    <div className="text-base font-mono font-bold text-slate-900">{fmtNum(b.count)}</div>
                    {b.unique_advertisers > 0 && (
                      <div className="text-[10px] text-slate-500">{b.unique_advertisers} brand{b.unique_advertisers === 1 ? "" : "s"}</div>
                    )}
                  </div>
                  <div className="w-32 text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sale</div>
                    <div className="text-base font-mono text-slate-900">{fmtMoney(b.sale_amount)}</div>
                  </div>
                  <div className="w-32 text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Payout</div>
                    <div className="text-base font-mono font-bold text-[#002FA7]">{fmtMoney(b.payout)}</div>
                    {pct > 0 && (
                      <div className="text-[10px] text-slate-500">{pct.toFixed(1)}% of total</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="mt-6 text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 rounded-md p-3">
        <b className="text-slate-700">Reading this view:</b>
        <ul className="list-disc ml-5 mt-1 space-y-0.5">
          <li><b>Approved + Bonus</b> = billing-grade revenue (matches <code className="font-mono bg-slate-200 px-1 rounded">?approved_only=true</code> on the click-log export).</li>
          <li><b>Pending</b> = gross attributed (matches the publisher Click-Log card totals).</li>
          <li><b>Cancelled + Non-Commission</b> = visible here but excluded from billing.</li>
          <li>If <b>Non-Commission</b> shows 0 rows but you expected some, click <b>Re-sync window</b> — pre-2026-06-17 ingest dropped them on the way in.</li>
        </ul>
        {data?.generated_at && (
          <div className="mt-2 text-[10px] text-slate-400 font-mono">Generated at {data.generated_at}</div>
        )}
      </div>
    </div>
  );
}
